/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Crn, CrnScope } from '../../../ts';

/**
 * Parse the crn
 * Any change made here should be done in packages/server-ts/src/helpers/crn.ts too
 *
 * @param {string} crn
 * @return {Crn} Object with crn metadata
 */
export const parse = (crn: string): Crn => {
  const parts = crn.split(':');
  const scopeParts = parts[6].split('/');
  let scope: CrnScope;

  switch (scopeParts[0]) {
    case 'a':
    case 'sub': // we are adding this to support watsonx Orchestrate CRNs
      scope = {
        account: scopeParts[1]
      };
      break;
    case 'o':
      scope = {
        org: scopeParts[1]
      };
      break;
    case 's':
      scope = {
        space: scopeParts[1]
      };
      break;
    default:
      scope = {};
      break;
  }

  return {
    crn: parts[0],
    version: parts[1],
    cname: parts[2],
    ctype: parts[3],
    service_name: parts[4],
    region: parts[5],
    scope: scope,
    service_instance: parts[7],
    resource_type: parts[8],
    resource: parts[9]
  };
};

export const isCrn = (crn: string): boolean =>
  typeof crn === 'string' && crn.startsWith('crn');

export const isCrnEncoded = (crn: string): boolean => {
  return isCrn(crn) && crn.startsWith('crn%3A');
};

/**
 * Get the CPD id from a crn or a CPD guid
 * The CPD guid in the crn looks like this "00000000-0000-0000-0000-001565723853"
 * This function extracts the last part "1565723853"
 * Any change made here should be done in packages/server-ts/src/helpers/crn.ts too
 *
 * @param {String} crn The crn or the guid to extract
 * @returns {String} the CPD id
 */
export const cpdIdFromCrn = (crn: string): string => {
  let guid = '';
  if (isCrn(crn)) {
    guid = parse(crn).service_instance;
  } else if (/^[0-9]{8}-[0-9]{4}-[0-9]{4}-[0-9]{4}-[0-9]{12}$/.test(crn)) {
    guid = crn;
  }
  return guid.split('-').join('').replace(/^0+/g, '');
};
